"use client"

import { useState } from "react"
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap"
import { FaPaperPlane, FaMapMarkerAlt, FaPhone, FaEnvelope, FaSpinner } from "react-icons/fa"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import emailjs from '@emailjs/browser';
import "./contactSection.css"

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" })
  const [submitted, setSubmitted] = useState(false)
  const [validated, setValidated] = useState(false)
  const [formStatus, setFormStatus] = useState("idle") 
  const [formRef, inViewForm] = useInView({ triggerOnce: false, threshold: 0.2 })
  const [infoRef, inViewInfo] = useInView({ triggerOnce: false, threshold: 0.2 })
  const [headerRef, inViewHeader] = useInView({ triggerOnce: false, threshold: 0.3 })


  const SERVICE_ID = "service_h240bm3"; 
  const TEMPLATE_ID = "template_8xw74ws"; 
  const PUBLIC_KEY = "bZyfe-qhgtOCXeIB7"; 


  emailjs.init("bZyfe-qhgtOCXeIB7");

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("loading");

    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "No especificado",
          message: formData.message,
          reply_to: formData.email, 
        },
        PUBLIC_KEY
      );

      setFormStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error al enviar:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 3000);
    }
  };

  const enviarPorCorreo = (tipo) => {
    console.log("Enviando por correo:", tipo)
    const destinatario = "contacto@reactiva.com.co"
    const asunto = `Contacto desde web - ${formData.name}`
    const cuerpo = `Nombre: ${formData.name}
Email: ${formData.email}
${formData.phone ? `Teléfono: ${formData.phone}` : ""}

Mensaje:
${formData.message}`

    let emailUrl = ""
    switch (tipo) {
      case "gmail":
        emailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(destinatario)}&su=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`
        break
      case "outlook":
        emailUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(destinatario)}&subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`
        break
      case "yahoo":
        emailUrl = `https://compose.mail.yahoo.com/?to=${encodeURIComponent(destinatario)}&subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`
        break
      default:
        emailUrl = `mailto:${encodeURIComponent(destinatario)}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`
    }

    console.log("URL generada:", emailUrl)
    window.open(emailUrl, "_blank")
    setFormData({ name: "", email: "", phone: "", message: "" })
    setFormStatus("success")
    setSubmitted(true)
    setValidated(false)
    setTimeout(() => {
      setFormStatus("idle")
      setSubmitted(false)
    }, 3000)
  }

  return (
    <section id="contact" className="modern-contact-section">
      <div className="contact-bg-animation">
        <div className="floating-contact-shapes">
          {[...Array(12)].map((_, i) => (
            <div key={i} className={`contact-shape contact-shape-${i + 1}`} />
          ))}
        </div>
        <div className="contact-gradient-orbs">
          <div className="contact-orb contact-orb-1" />
          <div className="contact-orb contact-orb-2" />
          <div className="contact-orb contact-orb-3" />
        </div>
      </div>

      <Container>
        <div className="contact-section-header" ref={headerRef}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="header-badge-contact"
          >
            <span>💬 Hablemos</span>
          </motion.div>
          <h2 className="contact-title">Contáctanos</h2>
          <p className="contact-subtitle">Estamos listos para ayudarte en tu próximo proyecto</p>
        </div>

        <Row className="justify-content-center contact-main-row">
          <Col lg={6}>
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, x: -30 }}
              animate={inViewForm ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="modern-contact-form-container"
            >
              {submitted && (
                <Alert
                  variant="success"
                  onClose={() => setSubmitted(false)}
                  dismissible
                  className="modern-alert-message"
                >
                  ¡Gracias por contactarnos! Te responderemos a la brevedad.
                </Alert>
              )}

              <div className="form-header">
                <h3>Envíanos un mensaje</h3>
                <p>Completa el formulario y nos pondremos en contacto contigo</p>
              </div>

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-4" controlId="formName">
                  <Form.Label className="modern-form-label">Nombre completo</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingresa tu nombre"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="modern-form-control"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="formEmail">
                  <Form.Label className="modern-form-label">Correo electrónico</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Ingresa tu email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="modern-form-control"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="formPhone">
                  <Form.Label className="modern-form-label">Teléfono (opcional)</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Ingresa tu teléfono"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="modern-form-control"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="formMessage">
                  <Form.Label className="modern-form-label">Mensaje</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Cuéntanos tu idea..."
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="modern-form-control"
                  />
                </Form.Group>

                <div className="text-center">
                  <Button
                    type="submit"
                    className="modern-submit-btn"
                    disabled={formStatus === "loading"}
                    onClick={(e) => {
                      console.log("Click directo en botón")
                      if (formStatus !== "loading") {
                        handleSubmit(e)
                      }
                    }}
                  >
                    {formStatus === "idle" && (
                      <>
                        Enviar mensaje <FaPaperPlane className="ms-2" />
                      </>
                    )}
                    {formStatus === "loading" && (
                      <>
                        <FaSpinner className="fa-spin me-2" />
                        Validando...
                      </>
                    )}
                    {formStatus === "error" && <>Complete todos los campos</>}
                    {formStatus === "options" && <>Seleccione su cliente de correo</>}
                    {formStatus === "success" && <>¡Mensaje enviado!</>}
                  </Button>
                </div>

                {formStatus === "options" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="mt-4"
                  >
                    <div className="text-center mb-3">
                      <p className="mb-3" style={{ color: "white", fontWeight: "bold" }}>
                        Elija su cliente de correo preferido:
                      </p>
                      <div className="email-buttons-container">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log("Click en Gmail")
                            enviarPorCorreo("gmail")
                          }}
                          className="email-option-btn gmail-btn"
                        >
                          Gmail
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log("Click en Outlook")
                            enviarPorCorreo("outlook")
                          }}
                          className="email-option-btn outlook-btn"
                        >
                          Outlook
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log("Click en Yahoo")
                            enviarPorCorreo("yahoo")
                          }}
                          className="email-option-btn yahoo-btn"
                        >
                          Yahoo Mail
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            console.log("Click en Otro")
                            enviarPorCorreo("default")
                          }}
                          className="email-option-btn other-btn"
                        >
                          Otro
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </Form>
            </motion.div>
          </Col>

          <Col lg={6}>
            <div className="contact-info-and-map">
              <motion.div
                ref={infoRef}
                initial={{ opacity: 0, x: 30 }}
                animate={inViewInfo ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
                className="modern-contact-info-container"
              >
                <div className="info-header">
                  <h3>Información de contacto</h3>
                  <p>Encuentra todas las formas de comunicarte con nosotros</p>
                </div>

                <div className="contact-info-grid">
                  <div className="modern-contact-info-item">
                    <div className="modern-info-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="modern-info-content">
                      <h5>Dirección</h5>
                      <p>Cra 23 #62-39 Edificio Capitalia, Oficina 602A - Manizales, Caldas, Colombia</p>
                    </div>
                  </div>

                  <div className="modern-contact-info-item">
                    <div className="modern-info-icon">
                      <FaPhone />
                    </div>
                    <div className="modern-info-content">
                      <h5>Teléfono</h5>
                      <p>+57 3172233111</p>
                    </div>
                  </div>

                  <div className="modern-contact-info-item">
                    <div className="modern-info-icon">
                      <FaEnvelope />
                    </div>
                    <div className="modern-info-content">
                      <h5>Email</h5>
                      <p>contacto@reactiva.com.co</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inViewInfo ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
                className="modern-map-container"
              >
                <iframe
                  title="Ubicación en mapa"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3974.2767260855157!2d-75.48934172501846!3d5.058828194917872!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e47657dd82e0f35%3A0x683cfe845b066589!2sCapitalia%20Centro%20Empresarial!5e0!3m2!1ses-419!2sco!4v1750881062867!5m2!1ses-419!2sco"
                  loading="lazy"
                  style={{ width: "100%", height: "100%", border: 0 }}
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </motion.div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default ContactSection
